/**
 * Public URL for `/tracking/click` — the TikTok (or any ad) destination that
 * mints click_id and redirects to the offer lander.
 *
 * **Redirect host:** set `VITE_TRACKING_PUBLIC_ORIGIN` (e.g. `https://greenchx.online`)
 * when the dashboard runs on a different domain than the API/tracker. If unset, falls
 * back to `VITE_API_BASE_URL` or `window.location.origin` (see `getPublicApiOrigin`).
 */

const CAMPAIGN_MACRO = "__CAMPAIGN_ID__"
const TTCLID_MACRO = "__CLICKID__"

function _normalizeToOrigin(raw: string): string {
  const t = raw.trim()
  if (!t) return ""
  try {
    const u = new URL(t.includes("://") ? t : `https://${t}`)
    return `${u.protocol}//${u.host}`
  } catch {
    return t.replace(/\/$/, "")
  }
}

export function getPublicApiOrigin(): string {
  const fromEnv = import.meta.env.VITE_API_BASE_URL as string | undefined
  if (typeof fromEnv === "string" && fromEnv.trim()) {
    return _normalizeToOrigin(fromEnv)
  }
  if (typeof window !== "undefined" && window.location?.origin) {
    return window.location.origin
  }
  return ""
}

/**
 * Host for generated tracking links only — not used for API `apiFetch` calls.
 */
export function getTrackingRedirectOrigin(): string {
  const dedicated = import.meta.env
    .VITE_TRACKING_PUBLIC_ORIGIN as string | undefined
  if (typeof dedicated === "string" && dedicated.trim()) {
    return _normalizeToOrigin(dedicated)
  }
  return getPublicApiOrigin()
}

/**
 * TikTok destination URL: `o`, `c`, `ttclid`, and `atxt` (ad copy for the lander as `ascr=`).
 * `atxt` is left empty so media buyers can paste e.g. `Learn+more+about+AI+tools` before launch.
 */
export function buildTrackingClickUrl(
  publicSlug: string,
  options?: { adText?: string },
): string {
  const origin = getTrackingRedirectOrigin()
  const qs = new URLSearchParams({
    o: publicSlug,
    c: CAMPAIGN_MACRO,
    ttclid: TTCLID_MACRO,
  })
  qs.set("atxt", options?.adText ?? "")
  const base = origin.replace(/\/$/, "")
  return `${base}/tracking/click?${qs.toString()}`
}
